import { useEffect, useState } from "react";
import api from "@/api";
import { useLang } from "@/i18n";
import { ClientLayout } from "./ClientHome";
import { StretchHorizontal, Timer, ChevronRight } from "lucide-react";

function StretchExerciseCard({ ex, testid, durationSec, notes }) {
  const { t } = useLang();
  return (
    <details className="kc-card px-5 py-4 group" data-testid={testid}>
      <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-center gap-3">
        {ex.image_url ? (
          <img src={ex.image_url} alt={ex.name} className="w-14 h-14 rounded-xl object-cover shrink-0" loading="lazy" />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-secondary flex items-center justify-center shrink-0">
            <StretchHorizontal className="w-6 h-6 text-primary" />
          </div>
        )}
        <span className="flex-1 min-w-0">
          <span className="font-bold block truncate">{ex.name}</span>
          <span className="text-xs text-muted-foreground">
            {ex.muscle_group && <span className="mr-2">{ex.muscle_group}</span>}
            {durationSec != null && <span>{durationSec} {t("st.seconds")}</span>}
          </span>
        </span>
        <ChevronRight className="w-4 h-4 text-primary shrink-0 transition-transform group-open:rotate-90" />
      </summary>
      <div className="mt-3 space-y-2">
        {ex.description && <p className="text-sm text-muted-foreground">{ex.description}</p>}
        {notes && <p className="text-sm font-semibold text-primary">{notes}</p>}
      </div>
    </details>
  );
}

export default function ClientStretching() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [lib, setLib] = useState([]);

  useEffect(() => {
    api.get("/client/stretching").then((r) => setData(r.data)).catch(() => setData({ routine: null }));
    api.get("/stretch-exercises").then((r) => setLib(r.data)).catch(() => setLib([]));
  }, []);

  if (data === null) return <ClientLayout><p className="text-muted-foreground">{t("common.loading")}</p></ClientLayout>;

  const routine = data.routine;
  const total = routine ? Math.round(routine.exercises.reduce((s, e) => s + (e.duration_sec || 0), 0) / 60) : 0;

  return (
    <ClientLayout>
      <div data-testid="client-stretching-page" className="max-w-xl mx-auto w-full space-y-5">
        <h1 className="text-3xl font-black uppercase tracking-tight flex items-center gap-2">
          <StretchHorizontal className="w-7 h-7 text-primary" /> {t("st.title")}
        </h1>

        {routine ? (
          <>
            <div className="kc-card-dark p-5" data-testid="stretching-routine-header">
              <p className="kc-eyebrow !text-[#E0597F] mb-2">{t("st.routines")}</p>
              <h2 className="text-xl font-black uppercase tracking-tight">{routine.name}</h2>
              {routine.description && <p className="text-sm opacity-80 mt-1">{routine.description}</p>}
              <p className="text-xs font-semibold mt-3 inline-flex items-center gap-1.5 opacity-80">
                <Timer className="w-4 h-4" /> {t("st.total")}: ~{total} {t("st.min")} · {routine.exercises.length} {routine.exercises.length === 1 ? t("st.exerciseSing") : t("st.exercises").toLowerCase()}
              </p>
            </div>
            <div className="space-y-3" data-testid="stretching-exercises">
              {routine.exercises.map((ex, i) => (
                <StretchExerciseCard key={`${ex.exercise_id}-${i}`} ex={ex} testid={`stretching-exercise-${i}`}
                  durationSec={ex.duration_sec} notes={ex.notes} />
              ))}
            </div>
          </>
        ) : (
          <div className="kc-card p-5 flex items-center gap-3" data-testid="stretching-empty">
            <StretchHorizontal className="w-8 h-8 text-primary shrink-0" />
            <p className="text-sm text-muted-foreground">{t("st.noRoutine")}</p>
          </div>
        )}

        {lib.length > 0 && (
          <section className="space-y-3" data-testid="stretching-library">
            <div>
              <h2 className="kc-eyebrow">{t("st.library")}</h2>
              <p className="text-xs text-muted-foreground mt-1">{t("st.librarySub")}</p>
            </div>
            {lib.map((ex, i) => (
              <StretchExerciseCard key={ex._id} ex={ex} testid={`stretching-lib-${i}`} />
            ))}
          </section>
        )}
      </div>
    </ClientLayout>
  );
}
